/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, Fragment } from "react";
import FeedbackTab from "./FeedbackTab";
import CompletionDialog from "./CompletionDialog";
import SurveyDialog from "./SurveyDialog";
import {
  parseSurveyCondition,
  conditionsEngine,
  publish,
  checkInDateRange,
  sleep,
  getScreenContent,
} from "../util";
import { postSurvey, postEvent } from "../actions";
import {
  SURVEY_CONTENT,
  TOGGLE_SURVEY,
  RESET,
  ADVANCE_SURVEY,
  REVERT_SURVEY,
  ON_CHANGE,
  INIT,
  SHOW_SURVEY_COMPLETION,
  SHOW_AUTOPOP_UP,
} from "../constants";
import {
  useSurveyStateManager,
  useSurveyMutationObserver,
  useSurveySubscribeClickEvent,
  useSurveySubscribeHoverEvent,
  useSurveyMouseNotInViewPort,
  useSurveyIdleTimeout,
} from "./hooks";

function Survey(props) {
  const [state, dispatch] = useSurveyStateManager(props);

  const {
    isOpen,
    questions,
    questionIndex,
    selectedAnswer,
    isValid,
    render,
    isEndOfSurvey,
    showSurveyCompletion,
    surveyCompletion,
    surveyAutoPopUp,
    isIdle,
    isIdleTimeoutEnabled,
  } = state;

  const surveyId = useRef("");
  const surveyTitle = useRef("");
  const formData = useRef(new FormData());
  const hasClicked = useRef(false);
  const hasHovered = useRef(false);
  const hasAutoPoppedUp = useRef(false);
  const hasMouseLeftViewport = useRef(false);
  const hasTimedOut = useRef(false);
  const customCloseFunction = useRef(null);
  const customSubmitFunction = useRef(null);
  const isPIIRedactionEnabledRef = useRef(true);
  const surveyScreenContentSelector = useRef(null);

  const [inDateRange, setIsInDateRange] = useState(false);

  const bluePrintStyle =
    props.definition.survey_content.surveyDisplayConditions.blueprintStyle;
  const disableCloseButton = props.definition.survey_content
    .surveyDisplayConditions.disableCloseButton
    ? "disableCloseButton"
    : "";
  const surveyDateRange =
    props.definition.survey_content.surveyDisplayConditions.surveyDateRange;

  //needed to move these functions here so the custom hooks can receive them
  const handleOnClickCondition = () => {
    publish(`surveyClickEvent.${surveyId.current}`);
  };

  const handleOnHoverCondition = () => {
    publish(`surveyHoverEvent.${surveyId.current}`);
  };

  /**
   * Handles auto pop setting
   */
  const handleAutoPopUp = (isAutoPopUpEnabled = false) => {
    if (isAutoPopUpEnabled && !isOpen && !hasAutoPoppedUp.current) {
      dispatch({
        type: SHOW_AUTOPOP_UP,
        payload: {
          surveyAutoPopUp: true,
          isOpen: true,
        },
      });
      hasAutoPoppedUp.current = true;
    }
  };

  useSurveyMutationObserver({
    ...props,
    handleOnClickCondition,
    handleOnHoverCondition,
    hasHovered,
    hasClicked,
    hasMouseLeftViewport,
    hasTimedOut,
    dispatch,
  });

  useSurveySubscribeClickEvent({
    ...props,
    handleOnClickCondition,
    handleOnHoverCondition,
    hasHovered,
    hasClicked,
    hasMouseLeftViewport,
    hasTimedOut,
    dispatch,
    handleAutoPopUp,
  });

  useSurveySubscribeHoverEvent({
    ...props,
    handleOnClickCondition,
    handleOnHoverCondition,
    hasHovered,
    hasClicked,
    hasMouseLeftViewport,
    hasTimedOut,
    dispatch,
    handleAutoPopUp,
  });

  useSurveyMouseNotInViewPort({
    ...props,
    handleOnClickCondition,
    handleOnHoverCondition,
    hasHovered,
    hasClicked,
    hasMouseLeftViewport,
    hasTimedOut,
    dispatch,
    handleAutoPopUp,
  });

  useSurveyIdleTimeout({
    ...props,
    dispatch,
  });

  /**
   * Handles close function
   *
   * @return {null}
   */
  const handleOnClick = () => {
    if (customCloseFunction.current) {
      try {
        const custFunc = new Function(customCloseFunction.current);
        custFunc();
      } catch (e) {
        console.log(e);
      }
    }

    if (isOpen) {
      postEvent({
        component: "survey",
        title: surveyTitle.current,
        type: "close",
      });
      reset();
    } else {
      postEvent({
        component: "survey",
        title: surveyTitle.current,
        type: "open",
      });
    }

    dispatch({
      type: TOGGLE_SURVEY,
      payload: !isOpen,
    });
  };

  const handleCloseCompletionDialog = () => reset();

  /**
   * Handles advancing survey
   */
  const handleNextClick = () => {
    const answer = formData.current.get(
      questions[questionIndex + 1].questionTitle
    );
    let userAnswer = answer || null;
    let qIndex = null;

    if (
      !parseSurveyCondition(questions[questionIndex + 1], formData.current) &&
      !parseSurveyCondition(questions[questionIndex + 2], formData.current)
    ) {
      if (
        !parseSurveyCondition(questions[questionIndex + 2], formData.current)
      ) {
        formData.current.delete(questions[questionIndex + 1].questionTitle);

        qIndex = questionIndex + 3;
      } else if (
        !parseSurveyCondition(questions[questionIndex + 1], formData.current)
      ) {
        formData.current.delete(questions[questionIndex + 1].questionTitle);
        qIndex = questionIndex + 2;
      } else {
        qIndex = questionIndex + 1;
      }
    } else if (
      !parseSurveyCondition(questions[questionIndex + 1], formData.current)
    ) {
      formData.current.delete(questions[questionIndex + 1].questionTitle);
      qIndex = questionIndex + 2;
    } else {
      qIndex = questionIndex + 1;
    }

    dispatch({
      type: ADVANCE_SURVEY,
      payload: {
        selectedAnswer: userAnswer,
        formData,
        questionIndex: qIndex,
      },
    });
  };

  /**
   * Handles reverting survey
   */
  const handleBackClick = () => {
    let qIndex = null;
    let userAnswer = null;

    if (!parseSurveyCondition(questions[questionIndex - 1], formData.current)) {
      qIndex = questionIndex - 2;
      userAnswer = formData.current.get(
        questions[questionIndex - 2].questionTitle
      );
    } else {
      //delete the last answer of the survey if you're going back
      if (
        questionIndex === questions.length - 1 ||
        questionIndex === questions.length - 2
      ) {
        formData.current.delete(questions[questionIndex].questionTitle);
      }
      qIndex = questionIndex - 1;
      userAnswer = formData.current.get(
        questions[questionIndex - 1].questionTitle
      );
    }

    dispatch({
      type: REVERT_SURVEY,
      payload: {
        selectedAnswer: userAnswer,
        questionIndex: qIndex,
        formData,
        isEndOfSurvey: false,
      },
    });
  };

  /**
   * Handles updating state when questions are answered
   *
   * @param {Event} e - event object from input fields
   */
  const handleOnChange = (e) => {
    let isEndOfSurv = false;

    formData.current.set(
      questions[questionIndex].questionTitle,
      e.target.value
    );
    let userAnswer = e.target.value;

    //need to look ahead to see if the next question is the last question
    const nextQuestion = questions[questionIndex + 1]
      ? questions[questionIndex + 1]
      : {};

    const secondNextQuestion = questions[questionIndex + 2]
      ? questions[questionIndex + 2]
      : {};

    if (
      !parseSurveyCondition(nextQuestion, formData.current) &&
      !parseSurveyCondition(secondNextQuestion, formData.current)
    ) {
      isEndOfSurv = !questions[questionIndex + 3];
    } else if (!parseSurveyCondition(nextQuestion, formData.current)) {
      isEndOfSurv = !questions[questionIndex + 2];
    }

    dispatch({
      type: ON_CHANGE,
      payload: {
        selectedAnswer: userAnswer,
        isEndOfSurvey: isEndOfSurv,
      },
    });
  };

  /**
   * Handles submitting survey
   *
   * @param {Event} e - event object from form
   */
  const handleSubmit = (e) => {
    e.preventDefault();

    if (customSubmitFunction.current) {
      try {
        const custFunc = new Function(customSubmitFunction.current);
        custFunc();
      } catch (err) {
        console.log(err);
      }
    }

    if (selectedAnswer) {
      formData.current.set(
        questions[questionIndex].questionTitle,
        selectedAnswer
      );
    }
    postSurvey(
      surveyTitle.current,
      Object.fromEntries(formData.current),
      isPIIRedactionEnabledRef.current,
      getScreenContent(surveyScreenContentSelector.current)
    );
    if (Object.keys(surveyCompletion).length > 0) {
      dispatch({
        type: SHOW_SURVEY_COMPLETION,
      });
    } else {
      reset();
    }
  };

  /**
   * Handles resetting state to initial state
   */
  const reset = () => {
    formData.current = new FormData();
    dispatch({
      type: RESET,
    });
  };

  //hooks version of componentsDidUpdate
  useEffect(() => {
    (async () => {
      if (!props.definition) return;

      const { definition } = props;

      //this section handles the initial setup after the component mounts
      const {
        surveyDisplayConditions: {
          autoPopUp,
          idleTimeOut,
          initialDelay,
          ...restSurveyDisplayConditions
        },
        surveyQuestions,
        surveyCompletion: contentSurveyCompletion,
        id,
        title,
        isPIIRedactionEnabled = true,
        screenContentSelector,
      } = definition[SURVEY_CONTENT];

      //ensure that this object exists before destructuring
      if (
        Object.prototype.hasOwnProperty.call(
          definition[SURVEY_CONTENT],
          "surveyCustomFunctions"
        )
      ) {
        const {
          surveyCustomFunctions: { customOnClose, customOnSubmit },
        } = definition[SURVEY_CONTENT];

        customCloseFunction.current = customOnClose || null;
        customSubmitFunction.current = customOnSubmit || null;
      }

      surveyId.current = id;
      surveyTitle.current = title;

      //here we need to run the conditions engine during setup
      // so we can determine if the conditions are set to render
      const engineOptions = {
        clickCallback: handleOnClickCondition,
        hoverCallback: handleOnHoverCondition,
      };

      handleAutoPopUp(autoPopUp);

      isPIIRedactionEnabledRef.current = isPIIRedactionEnabled;

      surveyScreenContentSelector.current = screenContentSelector;

      if (initialDelay) {
        await sleep(initialDelay);
      }

      // Check for a display condition date range. Set render appropriately.
      if (surveyDateRange) {
        setIsInDateRange(true);
        if (Object.keys(surveyDateRange).length > 1) {
          const isInRange = checkInDateRange(surveyDateRange);

          if (isInRange) setIsInDateRange(isInRange);
        }
      } else {
        setIsInDateRange(true);
      }

      const isRender = conditionsEngine(restSurveyDisplayConditions, {
        ...engineOptions,
        isHoveredTrue: hasHovered.current,
        isClickTrue: hasClicked.current,
        isMouseLeftViewportTrue: hasMouseLeftViewport.current,
        isIdle: hasTimedOut.current,
      });

      dispatch({
        type: INIT,
        payload: {
          render: isRender,
          surveyQuestions,
          surveyCompletion: contentSurveyCompletion || {},
          isIdleTimeoutEnabled: idleTimeOut && idleTimeOut >= 0,
        },
      });
    })();
  }, [props?.definition]);

  useEffect(() => {
    /** Added check for hasAutoPoppedUp.current to prevent this from
     * firing for Surveys opened by a mouseclick, as that open event is
     * already handled in handleOnClick()
     */
    if (hasAutoPoppedUp.current && render && inDateRange && isOpen && isIdle) {
      postEvent({
        component: "survey",
        title: surveyTitle.current,
        type: "open",
      });
    }
  }, [hasAutoPoppedUp.current, render, inDateRange, isOpen, isIdle]);

  return (
    /** We have to check both render and showSurveyCompletion in case we've set a one-time cookie with the Submit button.
     *  Otherwise, that cookie will set render to false and prevent the survey completion modal from ever displaying
     */
    (render || showSurveyCompletion) &&
    inDateRange && (
      <Fragment>
        <FeedbackTab
          bluePrintStyle={bluePrintStyle}
          surveyAutoPopUp={surveyAutoPopUp}
          questions={questions}
          isOpen={isOpen}
          handleOnClick={handleOnClick}
        />
        <CompletionDialog
          bluePrintStyle={bluePrintStyle}
          showSurveyCompletion={showSurveyCompletion}
          surveyCompletion={surveyCompletion}
          isOpen={isOpen}
          handleOnClick={handleOnClick}
          handleCloseCompletionDialog={handleCloseCompletionDialog}
        />
        <SurveyDialog
          surveyTitle={surveyTitle.current}
          surveyId={surveyId.current}
          bluePrintStyle={bluePrintStyle}
          disableCloseButton={disableCloseButton}
          isOpen={isOpen}
          showSurveyCompletion={showSurveyCompletion}
          handleOnClick={handleOnClick}
          questions={questions}
          questionIndex={questionIndex}
          handleSubmit={handleSubmit}
          selectedAnswer={selectedAnswer}
          handleOnChange={handleOnChange}
          isValid={isValid}
          handleBackClick={handleBackClick}
          isEndOfSurvey={isEndOfSurvey}
          handleNextClick={handleNextClick}
          isIdle={isIdle}
          isIdleTimeoutEnabled={isIdleTimeoutEnabled}
        />
      </Fragment>
    )
  );
}

export default Survey;
